import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ProjectCostSummary } from '@faktorplus/shared';
import { useActivityFeed, useCostsByProject, useDashboardSummary } from '../../api/reports';
import { Input, Select } from '../../components/ui/form';
import { AgingPanel, CostTrendPanel, InvoiceStatusPanel } from './dashboard-charts';
import { Panel, ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { Button } from '../../components/ui/button';
import { StatTile, StatTiles } from '../../components/ui/stat-tile';
import { StatusPill } from '../../components/ui/status-pill';
import { ClockAlertIcon, CoinsIcon, InboxIcon, UsersIcon } from '../../components/ui/icons';
import { formatDayCount, formatMoney, formatMoneyCompact, formatRelativeDay } from '../../lib/format';
import pageStyles from '../../components/layout/page.module.css';
import styles from './dashboard-page.module.css';

/**
 * The owner's home screen.
 *
 * Four tiles for the four questions he asks every morning, cost bars split by
 * category so it is obvious at a glance which site is driven by subcontractors
 * and which by materials, and a feed that answers "are they even clicking it".
 */
export function DashboardPage() {
  const { data: summary, isLoading, error } = useDashboardSummary();
  const [searchParams, setSearchParams] = useSearchParams();
  const period = resolvePeriod(searchParams);

  function updatePeriod(patch: Record<string, string>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  }

  if (isLoading) return <LoadingBlock />;
  if (error || !summary) return <ErrorBox>Nie udało się pobrać pulpitu.</ErrorBox>;

  return (
    <>
      <header className={pageStyles.header}>
        <h1 className={pageStyles.title}>{currentMonthLabel()}</h1>
        <span className={pageStyles.subtitle}>stan na dziś</span>
        <div className={pageStyles.actions}>
          <PeriodSelect period={period} onChange={updatePeriod} />
        </div>
      </header>

      <StatTiles>
        <StatTile
          to="/do-zatwierdzenia"
          tone="accent"
          icon={<InboxIcon />}
          label="Czeka na Ciebie"
          value={String(summary.awaitingMyApproval.count)}
          note={formatMoney(summary.awaitingMyApproval.amount)}
        />

        <StatTile
          to="/rozrachunki"
          tone={summary.overdue.count > 0 ? 'critical' : 'default'}
          icon={<ClockAlertIcon />}
          label="Po terminie"
          value={String(summary.overdue.count)}
          note={
            summary.overdue.count > 0
              ? `${formatMoney(summary.overdue.amount)} · ${formatDayCount(summary.overdue.maxDaysLate)}`
              : 'wszystko w terminie'
          }
        />

        <StatTile
          to="/faktury?status=ASSIGNED"
          icon={<UsersIcon />}
          label="U kierowników"
          value={String(summary.awaitingManagers.count)}
          note={
            summary.awaitingManagers.count > 0
              ? `najdłużej ${formatDayCount(summary.awaitingManagers.maxDaysWaiting)}`
              : 'nic nie czeka'
          }
        />

        <StatTile
          icon={<CoinsIcon />}
          label="Koszty w tym miesiącu"
          value={formatMoneyCompact(summary.currentMonth.netAmount)}
          note={`netto, ${summary.currentMonth.invoiceCount} faktur`}
        />
      </StatTiles>

      <div className={styles.split}>
        <div className={styles.charts}>
          <CostTrendPanel />
          <InvoiceStatusPanel period={period} />
          <AgingPanel />
          <ProjectCostsPanel period={period} />
        </div>

        <ActivityPanel />
      </div>
    </>
  );
}

function ProjectCostsPanel({ period }: { period: { from?: string; to?: string } }) {
  const { data: projects = [], isLoading, error } = useCostsByProject(period);

  const largest = Math.max(...projects.map((project) => project.netAmount), 1);

  return (
    <Panel title="Koszty budów">
      {/* An outage must not read as "this month cost nothing". */}
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorBox>Nie udało się pobrać kosztów budów.</ErrorBox>
      ) : projects.length === 0 ? (
        <p className={pageStyles.muted}>Brak danych kosztowych.</p>
      ) : (
        <>
          {projects.slice(0, 8).map((project) => (
            <CostBar key={project.projectId ?? 'overhead'} project={project} largest={largest} />
          ))}
          <div className={styles.legend}>
            {COST_SEGMENTS.map((segment) => (
              <span key={segment.key} className={styles.legendItem}>
                <span className={styles.swatch} style={{ background: segment.color }} />
                {segment.label}
              </span>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}

/**
 * Three buckets, because that is the split the client actually reasons about.
 * Each is a distinct hue; hovering a slice shows its own amount.
 */
const COST_SEGMENTS = [
  { key: 'materials', label: 'Materiały', color: 'var(--seg-materials)' },
  { key: 'subcontractors', label: 'Podwykonawcy', color: 'var(--seg-subcontractors)' },
  { key: 'other', label: 'Paliwo, sprzęt, inne', color: 'var(--seg-other)' },
] as const;

function CostBar({ project, largest }: { project: ProjectCostSummary; largest: number }) {
  const share = (amount: number) => `${(amount / largest) * 100}%`;
  const bucket = (name: string) =>
    project.breakdown
      .filter((entry) => entry.categoryName === name)
      .reduce((total, entry) => total + entry.netAmount, 0);

  const materials = bucket('Materiały');
  const subcontractors = bucket('Podwykonawca');
  const amounts: Record<(typeof COST_SEGMENTS)[number]['key'], number> = {
    materials,
    subcontractors,
    other: project.netAmount - materials - subcontractors,
  };

  return (
    <div className={styles.barRow}>
      <span className={styles.barName}>
        {/* Company-wide costs have no site to open. */}
        {project.projectId ? (
          <Link to={`/budowy/${project.projectId}`} className={styles.barLink}>
            {project.projectName}
          </Link>
        ) : (
          project.projectName
        )}
      </span>
      <span className={styles.track}>
        {COST_SEGMENTS.map((segment) => {
          const amount = amounts[segment.key];
          if (amount <= 0) return null;

          return (
            <span
              key={segment.key}
              className={styles.segment}
              style={{ width: share(amount), background: segment.color }}
            >
              <span className={styles.segTip}>
                <span className={styles.segTipLabel}>{segment.label}</span>
                <span className={styles.segTipValue}>{formatMoney(amount)}</span>
              </span>
            </span>
          );
        })}
      </span>
      <span className={styles.barValue}>{formatMoneyCompact(project.netAmount)}</span>
    </div>
  );
}

const ACTIVITY_PAGE_SIZE = 10;

function ActivityPanel() {
  const { data: entries = [], isLoading, error } = useActivityFeed();
  const [page, setPage] = useState(1);

  const pageCount = Math.max(1, Math.ceil(entries.length / ACTIVITY_PAGE_SIZE));
  const current = Math.min(page, pageCount);
  const shown = entries.slice((current - 1) * ACTIVITY_PAGE_SIZE, current * ACTIVITY_PAGE_SIZE);

  return (
    <Panel title="Co się dzieje">
      {isLoading && <LoadingBlock />}
      {error && <ErrorBox>Nie udało się pobrać aktywności.</ErrorBox>}
      {!isLoading && !error && entries.length === 0 && (
        <p className={pageStyles.muted}>Brak aktywności.</p>
      )}
      {shown.map((entry) => (
        <div key={entry.id} className={styles.feedItem}>
          <div className={styles.feedMain}>
            <span className={styles.feedVendor}>{entry.vendorName}</span>
            <span className={styles.feedDoc}>{entry.invoiceNumber}</span>
          </div>
          <span className={styles.feedAmount}>{formatMoney(entry.amount)}</span>
          <div className={styles.feedFooter}>
            <StatusPill status={entry.status} />
            <span className={styles.feedMeta}>
              {entry.userName} · {formatRelativeDay(entry.createdAt)}
            </span>
          </div>
        </div>
      ))}

      {entries.length > ACTIVITY_PAGE_SIZE && (
        <div className={pageStyles.pagination}>
          <Button disabled={current <= 1} onClick={() => setPage(current - 1)}>
            ← Nowsze
          </Button>
          <span>
            Strona {current} z {pageCount}
          </span>
          <span className={pageStyles.paginationSpacer} />
          <Button disabled={current >= pageCount} onClick={() => setPage(current + 1)}>
            Starsze →
          </Button>
        </div>
      )}
    </Panel>
  );
}

type PeriodMode = 'month' | 'halfyear' | 'year' | 'custom';

interface ResolvedPeriod {
  mode: PeriodMode;
  from?: string;
  to?: string;
}

const PERIOD_OPTIONS: { value: PeriodMode; label: string }[] = [
  { value: 'month', label: 'Ten miesiąc' },
  { value: 'halfyear', label: 'Ostatnie 6 miesięcy' },
  { value: 'year', label: 'Ten rok' },
  { value: 'custom', label: 'Własny zakres' },
];

/**
 * The window every cost figure on the dashboard is read through, stored in the
 * URL so a view can be re-opened. A preset is just its start date; `custom`
 * carries an explicit `from`/`to`. No range at all defaults to the running year.
 */
function resolvePeriod(params: URLSearchParams): ResolvedPeriod {
  const range = params.get('range');
  if (range === 'month') return { mode: 'month', from: firstOfMonthsAgo(0) };
  if (range === 'halfyear') return { mode: 'halfyear', from: firstOfMonthsAgo(5) };
  if (range === 'custom') {
    return { mode: 'custom', from: params.get('from') || undefined, to: params.get('to') || undefined };
  }
  return { mode: 'year', from: firstOfYear() };
}

function PeriodSelect({
  period,
  onChange,
}: {
  period: ResolvedPeriod;
  onChange: (patch: Record<string, string>) => void;
}) {
  function pick(mode: PeriodMode) {
    // A preset clears any custom dates; `custom` keeps whatever is already set.
    if (mode === 'custom') onChange({ range: 'custom' });
    else onChange({ range: mode, from: '', to: '' });
  }

  return (
    <div className={styles.periodControl}>
      <Select
        className={styles.periodSelect}
        aria-label="Zakres dat"
        value={period.mode}
        onChange={(event) => pick(event.target.value as PeriodMode)}
      >
        {PERIOD_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>

      {period.mode === 'custom' && (
        <div className={styles.periodDates}>
          <Input
            type="date"
            aria-label="Od"
            max={period.to}
            value={period.from ?? ''}
            onChange={(event) => onChange({ from: event.target.value })}
          />
          <span className={styles.periodDash}>—</span>
          <Input
            type="date"
            aria-label="Do"
            min={period.from}
            value={period.to ?? ''}
            onChange={(event) => onChange({ to: event.target.value })}
          />
        </div>
      )}
    </div>
  );
}

function firstOfMonthsAgo(months: number): string {
  const now = new Date();
  return toDateInput(new Date(now.getFullYear(), now.getMonth() - months, 1));
}

function firstOfYear(): string {
  const now = new Date();
  return toDateInput(new Date(now.getFullYear(), 0, 1));
}

/** `<input type="date">` speaks `YYYY-MM-DD` in local time, never ISO with a Z. */
function toDateInput(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function currentMonthLabel(): string {
  const label = new Intl.DateTimeFormat('pl-PL', { month: 'long', year: 'numeric' }).format(
    new Date(),
  );
  return label.charAt(0).toUpperCase() + label.slice(1);
}
